import clsx from 'clsx'
import dynamic from 'next/dynamic'
import React, {
  FC,
  useEffect,
  useState,
} from 'react'
import { BaseReactPlayerProps } from 'react-player/base'

import SVGPause from '@Assets/SVG/Pause'
import SVGPlay from '@Assets/SVG/Play'
import SVGReplay from '@Assets/SVG/Replay'
import { Loading } from '@Components/CMS/Video'
import { Asset } from '@Generated/graphql'
import { useSplitTest } from '@Globals/SplitTests/Hooks'

import styles from './Video.module.scss'

const ReactPlayer = dynamic(() => import('react-player/lazy'))

export const VideoHorizontal: FC<{ asset?: Asset, isActive: boolean, isFullscreen: boolean }> = ({
  asset,
  isActive = false,
  isFullscreen = false,
}) => {
  const [playerEmbed, setPlayer] = useState<BaseReactPlayerProps>()
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!playerEmbed) {
      return
    }

    setIsPlaying(isActive)
  }, [isActive, playerEmbed])

  if (!asset) {
    return null
  }

  const toggle = (event) => {
    event.stopPropagation()
    setIsPlaying(prev => !prev)
  }

  const replay = (event) => {
    event.stopPropagation()
    const reactPlayer = playerEmbed?.player.player
    if (reactPlayer) {
      reactPlayer.player.time(0)
      reactPlayer.player.play()
    }
  }

  return (
    <div className={styles.videoRoot}>
      {isLoading && <Loading className={styles.loading} />}
      <div
        className={clsx(styles.videoContainer, isFullscreen && styles.fullscreen)}
      >

        <ReactPlayer
          controls={false}
          height="100%"
          onReady={player => setPlayer(player)}
          onStart={() => setIsLoading(false)}
          playing={isPlaying}
          previewTabIndex={-1}
          width="100%"
        />
        {playerEmbed && (
          <div className={styles.videoControls}>
            <button className={styles.videoControlButton} onClick={toggle} tabIndex={-1} type="button">
              {
                    isPlaying
                      ? <SVGPause />
                      : <SVGPlay />
                  }
            </button>
            <button
              className={styles.videoControlButton}
              onClick={replay}
              tabIndex={-1}
              type="button"
            >
              {' '}
              <SVGReplay />
            </button>
          </div>
        )}

      </div>
    </div>
  )
}

export const VideoVertical: FC<{ asset?: Asset, size?: number }> = ({ asset, size }) => {
  const pdpGalleryV2 = useSplitTest('pdp_gallery_v2', 'b')
  if (!asset) {
    return null
  }

  return (

    <>
      <img
        alt={`Video Asset - :${asset.url}`}
        height={`${size}px`}
        src={`${asset.url}?width=78&quality=60,90`}
        width={`${size}px`}
      />
      <div className={styles.videoWrapperA}>
        {pdpGalleryV2
          ? (
            <>
              <svg className={styles.videoIconMobile} fill="none" height="32" viewBox="0 0 32 32" width="32" xmlns="http://www.w3.org/2000/svg">
                <rect fill="#FAF6F0" height="32" rx="16" width="32" />
                <path d="M12.5 21.1562L12.5 9.84375L22.9502 15.5L12.5 21.1562Z" fill="#FAF6F0" stroke="black" />
              </svg>
              <svg
                aria-hidden="true"
                className={styles.videoIconDesktop}
                preserveAspectRatio="xMidYMid meet"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M8 5.14v14l11-7l-11-7Z" fill="var(--white)" />
              </svg>
              <span className={styles.videoIconLabelDesktop}>VIDEO</span>
            </>

          )
          : (
            <>
              <svg
                aria-hidden="true"
                preserveAspectRatio="xMidYMid meet"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M8 5.14v14l11-7l-11-7Z" fill="var(--white)" />
              </svg>
              <span>VIDEO</span>
            </>
          )}
      </div>
    </>
  )
}
